import { getFirstPaginatedNode, getPaginatedNodes } from './utils';

import { GetQuotesQueryVariables } from '../gql/graphql';
import { graphql } from '../gql';
import { useMemo } from 'react';
import { useQuery } from '@apollo/client';

export const GET_QUOTES = graphql(`
  query getQuotes(
    $uuid: String
    $referenceId: String
    $submissionUuid: String
  ) {
    quotes(
      filters: {
        uuid: $uuid
        referenceId: $referenceId
        submissionUuid: $submissionUuid
        type: USER_QUOTE
      }
      sort: { order: "ASC", key: "order" }
    ) {
      pageInfo {
        totalResults
      }
      edges {
        cursor
        node {
          uuid
          status
          referenceId
          inForcePolicy
          endorsementImpact
          createdAt
          updatedAt
          type
          name
          order
          quotedPremium
          selectedQuotedPremium
          limit
          retention
          ratedPremium
          technicalPremium
          modifiedCoveragePremium
          isObsolete
          obsoleteAt
          excessConvergeLimit
          excessConvergePartOfLimit
          excessConvergeQuotaShare
          excessConvergeAttachmentPoint
          excessConvergeQuotedPremium
          excessConvergeIncreasedLimitFactor
          excessConvergeUnderlyingPremiumPercent
          excessConvergeQuotedToBenchmarkRatio
          excessTowerLayers {
            edges {
              cursor
              queryCursor
              node {
                primary
                attachmentPoint
                benchmarkPremium
                boundIncreasedLimitFactor
                boundPremium
                boundUnderlyingPercent
                companyShare
                createdAt
                limit
                minimumPremium
                name
                position
                updatedAt
                uuid
                participants(filters: { insurer: null, status: ACTIVE }) {
                  edges {
                    cursor
                    queryCursor
                    node {
                      referenceId
                      status
                      updatedAt
                      uuid
                      fundamentals {
                        edges {
                          cursor
                          node {
                            yearEstablished
                          }
                        }
                      }

                      insurer
                      quote {
                        policyReferenceId
                        attachmentPoint
                        benchmarkPremium
                        boundIncreasedLimitFactor
                        boundPremium
                        boundUnderlyingPercent
                        companyShare
                        limit
                        minimumPremium
                        uuid
                      }
                      aliases {
                        edges {
                          cursor
                          queryCursor
                          node {
                            createdAt
                            name
                            primary
                            source
                            sources
                            type
                            updatedAt
                            uuid
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
          coverages(sort: { key: "order", order: "ASC" }) {
            edges {
              cursor
              node {
                uuid
                name
                type
                order
                createdAt
                updatedAt
                endorsements {
                  edges {
                    cursor
                    node {
                      uuid
                      type
                      name
                      order
                      description
                      nonPremiumBearing
                      primary
                      excess
                      quote {
                        hazardGroup
                        basePremium
                        fusionScore
                        fusionScoreModifier
                        ratedFusionScoreModifier
                        selectedFusionScoreModifier
                        minFusionScoreModifier
                        maxFusionScoreModifier
                        included
                        limit
                        modifiedCoveragePremium
                        restorationPeriodDays
                        retention
                        technicalPremium
                        uuid
                        waitingPeriodHours
                        commissionAdjustmentFactor
                        enhancedMediaFactor
                        limitRetentionFactor
                        policyTermFactor
                        priorActsFactor
                        restorationPeriodFactor
                        sharedCoverageAggregateFactor
                        sharedPolicyAggregateFactor
                        subCoverageFactor
                        waitingPeriodFactor
                        retroactiveDate
                        ratedFusionScoreModifier
                        selectedFusionScoreModifier
                        fullPriorActs
                      }
                      createdAt
                      updatedAt
                    }
                  }
                }
                quote {
                  basePremium
                  commissionAdjustmentFactor
                  fusionScore
                  fusionScoreModifier
                  ratedFusionScoreModifier
                  selectedFusionScoreModifier
                  enhancedMediaFactor
                  hazardGroup
                  included
                  limit
                  limitRetentionFactor
                  maxFusionScoreModifier
                  minFusionScoreModifier
                  modifiedCoveragePremium
                  policyTermFactor
                  priorActsFactor
                  restorationPeriodDays
                  restorationPeriodFactor
                  retention
                  sharedCoverageAggregateFactor
                  sharedPolicyAggregateFactor
                  subCoverageFactor
                  technicalPremium
                  uuid
                  waitingPeriodFactor
                  waitingPeriodHours
                  retroactiveDate
                  ratedFusionScoreModifier
                  selectedFusionScoreModifier
                  fullPriorActs
                }
                subCoverages(sort: { key: "order", order: "ASC" }) {
                  edges {
                    cursor
                    node {
                      uuid
                      name
                      order
                      quote {
                        uuid
                        subLimit
                        subRetention
                        included
                      }
                    }
                  }
                }
              }
            }
          }
          subjectivities {
            edges {
              cursor
              node {
                uuid
                type
                name
                order
                primary
                excess
                description
                quote {
                  uuid
                  included
                }
              }
            }
          }
          endorsements {
            edges {
              cursor
              node {
                uuid
                type
                name
                order
                description
                nonPremiumBearing
                primary
                excess
                quote {
                  hazardGroup
                  basePremium
                  fusionScore
                  fusionScoreModifier
                  ratedFusionScoreModifier
                  selectedFusionScoreModifier
                  minFusionScoreModifier
                  maxFusionScoreModifier
                  included
                  limit
                  modifiedCoveragePremium
                  restorationPeriodDays
                  retention
                  technicalPremium
                  uuid
                  waitingPeriodHours
                  commissionAdjustmentFactor
                  enhancedMediaFactor
                  mediaEventCoverageOption
                  limitRetentionFactor
                  policyTermFactor
                  priorActsFactor
                  restorationPeriodFactor
                  sharedCoverageAggregateFactor
                  sharedPolicyAggregateFactor
                  subCoverageFactor
                  waitingPeriodFactor
                  retroactiveDate
                  ratedFusionScoreModifier
                  selectedFusionScoreModifier
                  fullPriorActs
                }
                createdAt
                updatedAt
              }
            }
          }
          enterpriseRatings(sort: { key: "name", order: "ASC" }) {
            edges {
              cursor
              node {
                uuid
                name
                quote {
                  uuid
                  riskLevel
                  createdAt
                  updatedAt
                }
                createdAt
                updatedAt
              }
            }
          }
          scheduleRatings(sort: { key: "name", order: "ASC" }) {
            edges {
              cursor
              node {
                uuid
                name
                quote {
                  uuid
                  percentage
                  createdAt
                  updatedAt
                }
                createdAt
                updatedAt
              }
            }
          }
        }
      }
    }
  }
`);

export function useGetQuotesQuery(variables: GetQuotesQueryVariables) {
  const { loading, error, data, refetch, startPolling, stopPolling, called } =
    useQuery(GET_QUOTES, {
      variables,
      fetchPolicy: 'no-cache',
    });

  const { quotes, quote, totalQuotes } = useMemo(() => {
    return {
      quotes: getPaginatedNodes(data?.quotes),
      quote: getFirstPaginatedNode(data?.quotes),
      totalQuotes: data?.quotes.pageInfo?.totalResults,
    };
  }, [data]);

  return {
    error,
    loading,
    data,
    quote,
    quotes,
    totalQuotes,
    refetch,
    startPolling,
    stopPolling,
    called,
  };
}
